import PropTypes from 'prop-types';
import { RFIS_SORTED_STATUSES, RFIS_INNER_STATUSES } from '../status-types';

export const documentVersionType = PropTypes.number;

export const statusType = PropTypes.oneOf(RFIS_SORTED_STATUSES);

export const innerStatusType = PropTypes.oneOf(RFIS_INNER_STATUSES);

export const historyAttributesType = PropTypes.object;

export const storeAttributesType = PropTypes.object;

export const reactRouterParamsType = PropTypes.shape({
  splat: PropTypes.string,
});

export const queryParamsType = PropTypes.shape({
  sort: PropTypes.string.isRequired,
  statusFilter: PropTypes.arrayOf(PropTypes.string),
  assignedToFilter: PropTypes.arrayOf(PropTypes.string),
  createdAtStartFilter: PropTypes.string,
  createdAtEndFilter: PropTypes.string,
});

export const locationAttributesType = PropTypes.object;

const coreIssueAttributesType = {
  dueDate: PropTypes.string,
  locationDescription: PropTypes.string,
  status: statusType.isRequired,
  innerStatus: innerStatusType,
  title: PropTypes.string.isRequired,
};

export const pendingIssueAttributesType = PropTypes.shape({
  ...coreIssueAttributesType,
  assignedTo: PropTypes.string.isRequired,
  assignedToType: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  customIdentifier: PropTypes.string,
});

export const pendingIssueFormAttributesType = PropTypes.object;

export const issueAttributesType = PropTypes.shape({
  ...coreIssueAttributesType,
  type: PropTypes.string.isRequired,
  answer: PropTypes.string,
  answeredAt: PropTypes.string,
  answeredByDisplayName: PropTypes.string,
  assignedToDisplayName: PropTypes.string,
  attachmentCount: PropTypes.number.isRequired,
  commentCount: PropTypes.number.isRequired,
  createdByDisplayName: PropTypes.string,
  permittedAttributes: PropTypes.array.isRequired,
  permittedStatuses: PropTypes.array.isRequired,
  permittedTransitions: PropTypes.array,
  permittedActions: PropTypes.oneOfType([PropTypes.array, PropTypes.object]), // TODO: remove array when move to v2
  customIdentifier: PropTypes.string,
  workflowState: PropTypes.shape({
    title: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
  }),
});

export const currentDocumentType = PropTypes.shape({
  bubbleUrn: PropTypes.string,
  fileName: PropTypes.string,
  name: PropTypes.string,
  urn: PropTypes.string,
  version: PropTypes.number,
});

export const currentDocumentThumbnailType = PropTypes.shape({
  attachmentUrl: PropTypes.string.isRequired,
  attachmentUrn: PropTypes.string.isRequired,
});

export const commentAttributesType = PropTypes.shape({
  body: PropTypes.string.isRequired,
  createdBy: PropTypes.string.isRequired,
  issueId: PropTypes.string,
});

//const changesetAttributes = ['status', 'answer', 'dueDate', 'assignedTo', 'assignedToType'];
const changesetAttributesType = PropTypes.shape({
  status: PropTypes.arrayOf(statusType),
  answer: PropTypes.arrayOf(PropTypes.string),
  dueDate: PropTypes.arrayOf(PropTypes.string),
  assignedTo: PropTypes.arrayOf(PropTypes.string),
  assignedToType: PropTypes.arrayOf(PropTypes.string),
});

export const changesetType = PropTypes.shape({
  createdAt: PropTypes.string.isRequired,
  createdBy: PropTypes.string,
  createdByDisplayName: PropTypes.string,
  event: PropTypes.oneOf(['create', 'update']),
  changes: changesetAttributesType.isRequired,
  changesWithOxygen: changesetAttributesType.isRequired,
});

export const channelItemsType = PropTypes.array;
export const issuesSortOrderArrayType = PropTypes.arrayOf(PropTypes.string);

const channelItemWrapperArrayType = PropTypes.shape({
  error: PropTypes.object,
  loading: PropTypes.bool.isRequired,
  sortOrder: issuesSortOrderArrayType.isRequired,
  collection: PropTypes.object.isRequired,
});

export const issueWrapperType = PropTypes.shape({
  error: PropTypes.object,
  loading: PropTypes.bool.isRequired,
  updating: PropTypes.bool.isRequired,
  channelItems: channelItemWrapperArrayType,
  attributes: issueAttributesType,
});

const objectWithName = PropTypes.shape({
  name: PropTypes.string.isRequired,
});

export const companiesType = PropTypes.objectOf(objectWithName);

export const oxygenType = PropTypes.shape({
  users: PropTypes.objectOf(objectWithName).isRequired,
  roles: PropTypes.objectOf(objectWithName).isRequired,
  companies: companiesType.isRequired,
});

export const userType = PropTypes.shape({
  error: PropTypes.object,
  loading: PropTypes.bool,
  attributes: PropTypes.shape({
    displayName: PropTypes.string.isRequired,
    rfis: PropTypes.shape({
      workflowRoles: PropTypes.arrayOf(PropTypes.string),
      workflowType: PropTypes.string,
      new: PropTypes.object,
    }),
  }),
});

export const documentType = PropTypes.shape({
  name: PropTypes.string.isRequired,
  version: documentVersionType.isRequired,
  latestVersion: documentVersionType,
  is2d: PropTypes.bool,
  fileName: PropTypes.string,
});

export const documentCollectionType = PropTypes.objectOf(documentType);

export const issuesFetchParamsType = PropTypes.shape({
  offset: PropTypes.number.isRequired,
  limit: PropTypes.number.isRequired,
  targetUrn: PropTypes.string,
  documentVersion: documentVersionType,
  queryParams: queryParamsType.isRequired,
});

export const issueArrayType = PropTypes.arrayOf(issueAttributesType);

export const issueWrapperArrayType = PropTypes.arrayOf(issueWrapperType);

export const transitionType = PropTypes.object;

export const newModalDataType = PropTypes.shape({
  isOpen: PropTypes.bool.isRequired,
});

export const sheetObjectType = PropTypes.shape({
  sheetGuid: PropTypes.string.isRequired,
  sheetName: PropTypes.string.isRequired,
});

export const statusOptionType = PropTypes.shape({
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  colorClassName: PropTypes.string.isRequired,
  description: PropTypes.string,
  transitionId: PropTypes.string,
});

export const sortPropType = PropTypes.shape({
  sortField: PropTypes.string,
  isAscending: PropTypes.bool,
});

export const statusOptionsArrayType = PropTypes.arrayOf(statusOptionType);

export const issueTransitionType = PropTypes.shape({
  transitionId: PropTypes.string,
  key: PropTypes.string,
});

export const roleType = PropTypes.shape({
  id: PropTypes.string.isRequired,
  oxygenId: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
});

export const companyType = PropTypes.shape({
  id: PropTypes.string.isRequired,
  oxygenId: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
});

export const userCompanyType = PropTypes.shape({
  id: PropTypes.string.isRequired,
  oxygenId: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
});

export const oxygenUserType = PropTypes.shape({
  id: PropTypes.string.isRequired,
  oxygenId: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  projectStatus: PropTypes.string.isRequired,
  profileImg50: PropTypes.string.isRequired,
  firstName: PropTypes.string.isRequired,
  lastName: PropTypes.string.isRequired,
  email: PropTypes.string.isRequired,
  isProjectAdmin: PropTypes.bool.isRequired,
  company: userCompanyType.isRequired,
  roles: PropTypes.arrayOf(roleType).isRequired,
});

export const pushpinExtensionType = PropTypes.shape({
  updatePattern: PropTypes.func.isRequired,
  createItem: PropTypes.func.isRequired,
  setVisibleById: PropTypes.func.isRequired,
  selectOne: PropTypes.func.isRequired,
  updateItemById: PropTypes.func.isRequired,
  showAll: PropTypes.func.isRequired,
  setDraggableById: PropTypes.func.isRequired,
  addEventListener: PropTypes.func.isRequired,
  removeEventListener: PropTypes.func.isRequired,
  selectNone: PropTypes.func.isRequired,
  removeItemById: PropTypes.func.isRequired,
  startCreateItem: PropTypes.func.isRequired,
  endCreateItem: PropTypes.func.isRequired,
  pushPinManager: PropTypes.shape({
    getItemById: PropTypes.func.isRequired,
    getSelectedItem: PropTypes.func.isRequired,
    items: PropTypes.func.isRequired,
  }),
});

export const documentForPanelType = PropTypes.shape({
  /**
   * The name of the document
   */
  name: PropTypes.string.isRequired,
  /**
   * The full file name with extension.
   */
  fileName: PropTypes.string.isRequired,
  /**
   * Whether the document is 2d, used for analytics.
   */
  is2d: PropTypes.bool.isRequired,
  /**
   * The version of the document currently being viewed.
   */
  version: PropTypes.number.isRequired,
  /**
   * The document's most recent version, When the `latestVersion` is different from the `version` attribute it wont let you create new Issue.
   */
  latestVersion: PropTypes.number.isRequired,
});
